import { useState } from 'react'
import AccessibleButton from './AccessibleButton'
import TurnstileWidget from './TurnstileWidget'

interface QuoteFormProps {
  serviceType: 'solar' | 'ev-charging'
  onSubmit?: (formData: QuoteFormData) => void
}

interface QuoteFormData {
  name: string
  email: string
  phone: string
  postcode: string
  propertyType: string
  serviceSpecific: {
    // Solar specific
    roofType?: string
    energyBill?: string
    // EV specific
    vehicleType?: string
    parkingType?: string
  }
  message: string
  turnstileToken: string
  privacyPolicyAccepted: boolean
}

const QuoteForm = ({ serviceType, onSubmit }: QuoteFormProps) => {
  const [formData, setFormData] = useState<QuoteFormData>({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    propertyType: '',
    serviceSpecific: {},
    message: '',
    turnstileToken: '',
    privacyPolicyAccepted: true
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (name.startsWith('serviceSpecific.')) {
      const field = name.replace('serviceSpecific.', '')
      setFormData(prev => ({
        ...prev,
        serviceSpecific: {
          ...prev.serviceSpecific,
          [field]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.turnstileToken) {
      setSubmitMessage('Please complete the security verification.')
      return
    }

    setIsSubmitting(true)
    setSubmitMessage('')

    try {
      const quoteApiUrl = import.meta.env.VITE_QUOTE_API_URL

      if (!quoteApiUrl) {
        throw new Error('Quote API URL not configured')
      }

      const response = await fetch(quoteApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          serviceType,
          timestamp: new Date().toISOString()
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      console.log('Quote form submitted successfully:', responseData)

      if (onSubmit) {
        onSubmit(formData)
      }

      setSubmitMessage('Thank you! Your quote request has been submitted. We\'ll contact you within 24 hours.')

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        postcode: '',
        propertyType: '',
        serviceSpecific: {},
        message: '',
        turnstileToken: '',
        privacyPolicyAccepted: true
      })
    } catch (error) {
      console.error('Error submitting quote form:', error)
      setSubmitMessage('Sorry, there was an error submitting your request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleTurnstileSuccess = (token: string) => {
    setFormData(prev => ({
      ...prev,
      turnstileToken: token
    }))
  }

  const handleTurnstileError = () => {
    setFormData(prev => ({
      ...prev,
      turnstileToken: ''
    }))
  }

  const handleTurnstileExpire = () => {
    setFormData(prev => ({
      ...prev,
      turnstileToken: ''
    }))
  }

  const renderServiceSpecificFields = () => {
    if (serviceType === 'solar') {
      return (
        <>
          <div>
            <label htmlFor="roofType" className="block text-sm font-medium text-gray-700 mb-2">
              Roof Type
            </label>
            <select
              id="roofType"
              name="serviceSpecific.roofType"
              value={formData.serviceSpecific.roofType || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
            >
              <option value="">Select roof type</option>
              <option value="pitched-tile">Pitched Tile</option>
              <option value="pitched-slate">Pitched Slate</option>
              <option value="flat">Flat Roof</option>
              <option value="metal">Metal Roof</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="energyBill" className="block text-sm font-medium text-gray-700 mb-2">
              Monthly Energy Bill
            </label>
            <select
              id="energyBill"
              name="serviceSpecific.energyBill"
              value={formData.serviceSpecific.energyBill || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
            >
              <option value="">Select range</option>
              <option value="under-50">Under £50</option>
              <option value="50-100">£50 - £100</option>
              <option value="100-150">£100 - £150</option>
              <option value="150-200">£150 - £200</option>
              <option value="over-200">Over £200</option>
            </select>
          </div>
        </>
      )
    }

    if (serviceType === 'ev-charging') {
      return (
        <>
          <div>
            <label htmlFor="vehicleType" className="block text-sm font-medium text-gray-700 mb-2">
              Electric Vehicle Type
            </label>
            <select
              id="vehicleType"
              name="serviceSpecific.vehicleType"
              value={formData.serviceSpecific.vehicleType || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
            >
              <option value="">Select vehicle type</option>
              <option value="tesla">Tesla</option>
              <option value="bmw">BMW</option>
              <option value="audi">Audi</option>
              <option value="volkswagen">Volkswagen</option>
              <option value="nissan">Nissan</option>
              <option value="other">Other</option>
              <option value="planning-to-buy">Planning to buy</option>
            </select>
          </div>

          <div>
            <label htmlFor="parkingType" className="block text-sm font-medium text-gray-700 mb-2">
              Parking Situation
            </label>
            <select
              id="parkingType"
              name="serviceSpecific.parkingType"
              value={formData.serviceSpecific.parkingType || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
            >
              <option value="">Select parking type</option>
              <option value="driveway">Driveway</option>
              <option value="garage">Garage</option>
              <option value="allocated-space">Allocated Parking Space</option>
              <option value="street">Street Parking</option>
              <option value="other">Other</option>
            </select>
          </div>
        </>
      )
    }

    return null
  }

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="postcode" className="block text-sm font-medium text-gray-700 mb-2">
              Postcode *
            </label>
            <input
              type="text"
              id="postcode"
              name="postcode"
              value={formData.postcode}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
            />
          </div>
        </div>

        {/* Property Information */}
        <div>
          <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-2">
            Property Type
          </label>
          <select
            id="propertyType"
            name="propertyType"
            value={formData.propertyType}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
          >
            <option value="">Select property type</option>
            <option value="detached">Detached House</option>
            <option value="semi-detached">Semi-Detached House</option>
            <option value="terraced">Terraced House</option>
            <option value="bungalow">Bungalow</option>
            <option value="apartment">Apartment/Flat</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Service Specific Fields */}
        {renderServiceSpecificFields()}

        {/* Additional Information */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            Additional Information
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows={4}
            placeholder="Please provide any additional details about your requirements..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent resize-vertical"
          />
        </div>

        {/* Turnstile Security Check */}
        <TurnstileWidget
          onSuccess={handleTurnstileSuccess}
          onError={handleTurnstileError}
          onExpire={handleTurnstileExpire}
          className="flex justify-center"
        />

        {/* Submit Button */}
        <div className="pt-4">
          <AccessibleButton
            type="submit"
            disabled={isSubmitting}
            variant="primary"
            className="w-full py-3 text-lg"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                Submitting...
              </div>
            ) : (
              'Get My Free Quote'
            )}
          </AccessibleButton>
        </div>

        {/* Success/Error Message */}
        {submitMessage && (
          <div
            className={`p-4 rounded-lg ${
              submitMessage.includes('error')
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}
          >
            {submitMessage}
          </div>
        )}

        {/* Privacy Notice */}
        <div className="text-sm text-gray-600 border-t pt-6 mt-8">
          <p className="mb-2">
            <strong>Your privacy matters to us.</strong> We'll only use your information to provide you with a quote and follow up on your enquiry.
          </p>
          <p className="mb-2">
            By submitting this form, you consent to us contacting you about your enquiry in accordance with our{' '}
            <a href="/privacy-policy" className="text-primary-green underline">Privacy Policy</a>.
          </p>
          <p>
            This website uses cookies to enhance your experience. View our{' '}
            <a href="/cookies" className="text-primary-green underline">Cookie Policy</a> for more information.
          </p>
        </div>
      </form>
    </div>
  )
}

export default QuoteForm